import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getFieldByKey, type AggFn, type FlatEmployeeRow } from "@/lib/analytics/fieldCatalog";
import {
  computePivot,
  flatRows,
  pivotToChartData,
  cellKey,
  TOTAL_COL,
  HEADCOUNT_KEY,
  type PivotValueSpec,
} from "@/lib/analytics/pivot";
import type { ReportTile } from "./types";

// Module-local: only this file renders with them. Exporting non-components
// from a component file breaks Fast Refresh — tileExportRows, which callers do
// need, lives in ./tileExport.
const TILE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#14b8a6"];

function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return "–";
  return (Math.round(n * 100) / 100).toLocaleString();
}

export function PivotTileView({
  dataset,
  tile,
  isLoading,
}: {
  dataset: FlatEmployeeRow[];
  tile: ReportTile;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();
  const labelFor = (key: keyof FlatEmployeeRow): string => {
    const f = getFieldByKey(key as string);
    return f ? t(f.labelKey) : String(key);
  };
  const aggLabel = (agg: AggFn) => t(`analytics.pivot.agg.${agg}`);

  const pivot = useMemo(
    () => (tile.config.mode === "pivot" ? computePivot(dataset, tile.config) : null),
    [dataset, tile.config]
  );
  const flat = useMemo(
    () => (tile.config.mode === "flat" ? flatRows(dataset, tile.config, labelFor) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataset, tile.config]
  );
  const chartData = useMemo(() => (pivot ? pivotToChartData(pivot) : []), [pivot]);

  if (isLoading) {
    return <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">{t("analytics.loading")}</div>;
  }
  if (tile.config.mode === "flat") {
    return <FlatTable rows={flat} />;
  }
  if (!pivot || pivot.rows.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.pivot.noData")}</p>;
  }
  if (tile.output === "chart") {
    return <PivotChart data={chartData} type={tile.chartType} emptyLabel={t("analytics.pivot.noData")} />;
  }
  return <PivotTable pivot={pivot} labelFor={labelFor} aggLabel={aggLabel} fmt={fmtNum} t={t} />;
}

export function PivotChart({
  data,
  type,
  emptyLabel,
}: {
  data: { key: string; value: number }[];
  type: "bar" | "line" | "pie";
  emptyLabel: string;
}) {
  if (data.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">{emptyLabel}</p>;
  if (type === "pie") {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="key" cx="50%" cy="50%" outerRadius={100} label={({ key, percent }) => `${key} ${((percent ?? 0) * 100).toFixed(0)}%`} labelLine={false}>
            {data.map((_e, i) => <Cell key={i} fill={TILE_COLORS[i % TILE_COLORS.length]} />)}
          </Pie>
          <Legend />
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === "line") {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="key" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} angle={-30} textAnchor="end" />
          <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={48} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="key" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} angle={-30} textAnchor="end" />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={48} />
        <Tooltip />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((_e, i) => <Cell key={i} fill={TILE_COLORS[i % TILE_COLORS.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PivotTable({
  pivot,
  labelFor,
  aggLabel,
  fmt,
  t,
}: {
  pivot: ReturnType<typeof computePivot>;
  labelFor: (k: keyof FlatEmployeeRow) => string;
  aggLabel: (a: AggFn) => string;
  fmt: (n: number | null | undefined) => string;
  t: (k: string) => string;
}) {
  const hasCrossTab = !(pivot.colValues.length === 1 && pivot.colValues[0] === TOTAL_COL);
  const valueLabel = (spec: PivotValueSpec) =>
    spec.field === (HEADCOUNT_KEY as keyof FlatEmployeeRow)
      ? t("analytics.pivot.headcount")
      : `${aggLabel(spec.agg)}(${labelFor(spec.field)})`;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          {hasCrossTab && (
            <tr className="border-b">
              {pivot.rowFields.map((f) => (
                <th key={`spacer-${String(f)}`} className="py-2 px-3" />
              ))}
              {pivot.colValues.map((col) => (
                <th key={col} colSpan={pivot.valueSpecs.length} className="py-2 px-3 text-center font-semibold border-l">
                  {col}
                </th>
              ))}
            </tr>
          )}
          <tr className="border-b">
            {pivot.rowFields.map((f) => (
              <th key={String(f)} className="text-left py-2 px-3 font-medium">
                {labelFor(f)}
              </th>
            ))}
            {pivot.colValues.map((col) =>
              pivot.valueSpecs.map((spec, si) => (
                <th key={`${col}-${si}`} className={`text-right py-2 px-3 font-medium ${si === 0 ? "border-l" : ""}`}>
                  {valueLabel(spec)}
                </th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {pivot.rows.map((row, ri) => (
            <tr key={ri} className="border-b last:border-0 hover:bg-muted/40">
              {row.rowKey.map((rk, ki) => (
                <td key={ki} className="py-2 px-3">
                  {rk}
                </td>
              ))}
              {pivot.colValues.map((col) =>
                pivot.valueSpecs.map((spec, si) => (
                  <td key={`${col}-${si}`} className={`py-2 px-3 text-right tabular-nums ${si === 0 ? "border-l" : ""}`}>
                    {fmt(row.cells[col]?.[cellKey(spec)])}
                  </td>
                ))
              )}
            </tr>
          ))}
          <tr className="border-t-2 font-semibold bg-muted/30">
            {pivot.rowFields.map((f, i) => (
              <td key={String(f)} className="py-2 px-3">
                {i === 0 ? t("analytics.pivot.grandTotal") : ""}
              </td>
            ))}
            {pivot.colValues.map((col) =>
              pivot.valueSpecs.map((spec, si) => (
                <td key={`gt-${col}-${si}`} className={`py-2 px-3 text-right tabular-nums ${si === 0 ? "border-l" : ""}`}>
                  {fmt(pivot.grandTotals[col]?.[cellKey(spec)])}
                </td>
              ))
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function FlatTable({ rows }: { rows: Record<string, any>[] }) {
  if (rows.length === 0) return null;
  const headers = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto max-h-[460px] overflow-y-auto">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 bg-card">
          <tr className="border-b">
            {headers.map((h) => (
              <th key={h} className="text-left py-2 px-3 font-medium whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-muted/40">
              {headers.map((h) => (
                <td key={h} className="py-2 px-3 whitespace-nowrap">
                  {String(r[h] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
